import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useUpdateCustomerMutation } from 'src/backend';
import LoadingButton from 'src/components/LoadingButton';

export type Customer = {
  id: string;
  name: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
};

export type UpdateCustomerRequest = {
  name?: string;
};

interface EditCustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onCustomerUpdated: () => void;
}

function EditCustomerDialog({
  open,
  customer,
  onClose,
  onCustomerUpdated,
}: EditCustomerDialogProps) {
  const [editCustomer, setEditCustomer] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [updateCustomer, { isLoading: isUpdatingCustomer }] =
    useUpdateCustomerMutation();

  useEffect(() => {
    if (open && customer) {
      setEditCustomer({
        id: customer.id,
        name: customer.name,
      });
    } else if (!open) {
      setEditCustomer(null);
    }
  }, [open, customer]);

  const handleUpdateCustomer = () => {
    if (editCustomer) {
      const request: UpdateCustomerRequest = {
        name: editCustomer.name,
      };

      updateCustomer({ customerId: editCustomer.id, request })
        .unwrap()
        .then(() => {
          onCustomerUpdated();
          onClose();
        });
    }
  };

  const handleClose = () => {
    onClose();
    setEditCustomer(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
        Edit Customer
      </DialogTitle>
      <DialogContent sx={{ padding: '2rem' }}>
        <Stack spacing={3}>
          <TextField
            label="Name"
            value={editCustomer?.name || ''}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, name: e.target.value } : null,
              )
            }
            fullWidth
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={isUpdatingCustomer}
          variant="contained"
          onClick={handleUpdateCustomer}
          disabled={!editCustomer?.name}
        >
          Update Customer
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditCustomerDialog;
